import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStatusIcon } from '../../utils/statusIcons';
import { getTagTypeConfig } from '../../utils/tagIcons';

const PersonalizedTestList = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const Tag = ({ name, status, type }) => {
    const { bgClass } = getTagTypeConfig(type);
    const { icon, color } = getStatusIcon(status);
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${bgClass}`}>
        <span className={`material-symbols-outlined text-[14px] ${color}`}>{icon}</span>
        {name}
      </span>
    );
  };

  useEffect(() => {
    const fetchPersonalizedTests = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/client/personalized-tests/me?skip=0&limit=100`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setTests(result.data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalizedTests();
  }, []);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return {
          icon: 'task_alt',
          iconBg: 'bg-status-green text-white',
          cardBorder: 'border-slate-100 dark:border-slate-700',
          textColor: 'group-hover:text-status-green',
          buttonClass: 'bg-white border-2 border-status-green text-status-green hover:bg-green-50 dark:hover:bg-green-900/20',
          buttonText: 'Review',
          buttonIcon: 'visibility'
        };
      case 'created':
      default:
        return {
          icon: 'play_circle',
          iconBg: 'bg-primary-blue text-white',
          cardBorder: 'border-primary-blue/20',
          textColor: 'group-hover:text-primary-blue',
          buttonClass: 'bg-primary-blue hover:bg-blue-600 text-white',
          buttonText: 'Start Test',
          buttonIcon: 'play_arrow'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl text-red-600 dark:text-red-400">
        Error loading personalized tests: {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 relative pb-8">
      <div className="timeline-line"></div>
      {tests.map((test) => {
        const config = getStatusConfig(test.status);
        const targetUrl = test.status === 'completed' 
          ? `/personalized-tests/${test.id}/review` 
          : `/personalized-tests/${test.id}/attempt`;

        return (
          <div key={test.id} className="timeline-item group relative flex items-start gap-4 z-10">
            <div className={`shrink-0 flex items-center justify-center size-14 rounded-full shadow-md ${config.iconBg}`}>
              <span className="material-symbols-outlined text-3xl fill-1">{config.icon}</span>
            </div>
            <div className={`flex-1 flex flex-col md:flex-row md:items-center justify-between p-5 bg-white dark:bg-card-dark rounded-2xl border shadow-sm transition-all duration-200 gap-4 ${config.cardBorder}`}>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <h3 className={`text-lg font-bold transition-colors text-slate-900 dark:text-white ${config.textColor}`}>{test.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Created on {new Date(test.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                {/* Tags Sections */}
                {['vocabulary_topic', 'grammar', 'vocabulary'].map(type => {
                  const group = test.tags?.find(t => t.tag_type_code === type);
                  if (!group || !group.tags || group.tags.length === 0) return null;
                  
                  const { icon, label } = getTagTypeConfig(type);

                  return (
                    <div key={type} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-tight">
                        <span className="material-symbols-outlined text-sm">{icon}</span>
                        <span>{label}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {group.tags.map((tag, idx) => (
                          <Tag key={idx} name={tag.name} status={tag.status} type={type} />
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Exercise Types Section */}
                {test.exercise_types && test.exercise_types.length > 0 && (
                  <div className="flex flex-col gap-1.5 mt-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-tight">
                      <span className="material-symbols-outlined text-sm">lists</span>
                      <span>Exercises</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {test.exercise_types.map((ex) => (
                        <div key={ex.id} className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                          <div className="w-1 h-1 rounded-full bg-primary-blue"></div>
                          <span>{ex.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between md:justify-end gap-4 md:min-w-[200px]">
                <Link to={targetUrl} className={`flex items-center justify-center gap-2 h-10 px-6 rounded-xl font-medium transition-all shadow-sm min-w-[140px] ${config.buttonClass}`}>
                  <span>{config.buttonText}</span>
                  <span className="material-symbols-outlined text-lg">{config.buttonIcon}</span>
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PersonalizedTestList;
